import type { SignalSeverity } from "@/api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import { SIGNAL_SEVERITY_TONE } from "@/lib/risk-helpers";

interface SignalSeverityBadgeProps {
  severity: SignalSeverity | string | null | undefined;
}

// Each severity maps to a translation key so the badge label
// re-renders on a locale switch.
const SIGNAL_SEVERITY_KEY: Record<SignalSeverity, TranslationKey> = {
  low: "risk.severity.low",
  medium: "risk.severity.medium",
  high: "risk.severity.high",
  critical: "risk.severity.critical",
};

/** Display a risk signal severity with the Ocean Blue palette + a
 *  localized label. Falls back to neutral / em-dash when the
 *  severity is missing or unrecognised. */
export function SignalSeverityBadge({ severity }: SignalSeverityBadgeProps) {
  const { t } = useTranslation();
  if (!severity) {
    return <Badge tone="neutral">—</Badge>;
  }
  const known = severity as SignalSeverity;
  const key = SIGNAL_SEVERITY_KEY[known];
  // Fall back to the raw backend value when the severity is not one
  // of the known enum members.
  const label = key ? t(key) : severity;
  const tone = SIGNAL_SEVERITY_TONE[known] ?? "neutral";
  return <Badge tone={tone}>{label}</Badge>;
}
